import { useState, useCallback, useEffect } from 'react'
import BrandLogo from '../components/BrandLogo'
import {
  SlideIntro,
  SlideCategoryChooser,
  SlideShowsSummary,
  SlideShowsDetail,
  SlideCriticalMoments,
  SlideYourMusic,
} from './wrapped/WrappedSlides'

/* ═══════════════════════════════════════════════════════════════════════════
   DIZEE — Year in Review ("Wrapped")
   Password-protected, full-screen slide presentation.
   Mobile-first card carousel with swipe + keyboard nav.
   ═══════════════════════════════════════════════════════════════════════════ */

const WRAPPED_PASSWORD = 'dizee2026'

export default function WrappedPage() {
  const [authenticated, setAuthenticated] = useState(false)
  const [password, setPassword] = useState('')
  const [error, setError] = useState(false)
  const [currentSlide, setCurrentSlide] = useState(0)

  const TOTAL_SLIDES = 6

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault()
    if (password === WRAPPED_PASSWORD) {
      setAuthenticated(true)
      setError(false)
    } else {
      setError(true)
    }
  }

  const goNext = useCallback(() => {
    if (currentSlide < TOTAL_SLIDES - 1) {
      setCurrentSlide((s) => s + 1)
    }
  }, [currentSlide])

  const goPrev = useCallback(() => {
    if (currentSlide > 0) {
      setCurrentSlide((s) => s - 1)
    }
  }, [currentSlide])

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'ArrowRight' || e.key === ' ') goNext()
      if (e.key === 'ArrowLeft') goPrev()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [goNext, goPrev])

  const [touchStart, setTouchStart] = useState<number | null>(null)

  const onTouchStart = (e: React.TouchEvent) => setTouchStart(e.touches[0].clientX)
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStart === null) return
    const diff = touchStart - e.changedTouches[0].clientX
    if (Math.abs(diff) > 50) {
      diff > 0 ? goNext() : goPrev()
    }
    setTouchStart(null)
  }

  /* ── Password gate ──────────────────────────────────────────────────── */
  if (!authenticated) {
    return (
      <div
        className="deck-final-login fixed inset-0 flex items-center justify-center p-4 sm:p-8"
        style={{ backgroundColor: '#13120A' }}
      >
        <div className="w-full max-w-md">
          <div className="mb-10">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-5 h-5 bg-[#FF2300] rounded-sm flex-shrink-0" />
              <BrandLogo className="h-3 w-auto text-white" />
            </div>
            <p className="text-sm text-zinc-500">Enter the password to view this report.</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-sm text-zinc-300 mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  setError(false)
                }}
                placeholder="Password"
                autoFocus
                autoComplete="current-password"
                className="deck-final-input w-full rounded-lg border border-white/[0.08] bg-white/[0.03] px-4 py-3 text-base text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-[#FF2300]/50 focus:border-transparent"
              />
              {error && (
                <p className="mt-2 text-xs text-red-400">Incorrect password. Try again.</p>
              )}
            </div>
            <button
              type="submit"
              className="deck-final-input w-full rounded-lg bg-white px-4 py-3 text-base font-semibold text-zinc-950 hover:bg-zinc-100 transition-colors"
            >
              View Report
            </button>
          </form>
          <p className="mt-8 text-[11px] text-zinc-700">
            Confidential — For intended recipients only.
          </p>
        </div>
      </div>
    )
  }

  /* ── Slide content ──────────────────────────────────────────────────── */
  const slides = [
    <SlideIntro key="intro" onStart={goNext} />,
    <SlideCategoryChooser key="category" onSelect={goNext} />,
    <SlideShowsSummary key="shows-summary" />,
    <SlideShowsDetail key="shows-detail" />,
    <SlideCriticalMoments key="critical" />,
    <SlideYourMusic key="music" />,
  ]

  return (
    <div
      className="fixed inset-0 flex flex-col items-center justify-center overflow-hidden"
      style={{ backgroundColor: '#0A0A08' }}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Subtle ambient glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute w-[500px] h-[500px] rounded-full opacity-[0.06] blur-[120px]"
          style={{
            background: 'radial-gradient(circle, #FF2300 0%, transparent 70%)',
            top: '-15%',
            right: '-10%',
          }}
        />
        <div
          className="absolute w-[400px] h-[400px] rounded-full opacity-[0.04] blur-[100px]"
          style={{
            background: 'radial-gradient(circle, #FF2300 0%, transparent 70%)',
            bottom: '-15%',
            left: '-10%',
          }}
        />
      </div>

      {/* Slide container */}
      <div className="relative z-10 w-full max-w-[440px] px-4">
        {slides[currentSlide]}
      </div>

      {/* Progress dots */}
      <div className="relative z-10 flex items-center gap-2 mt-6">
        {Array.from({ length: TOTAL_SLIDES }).map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrentSlide(i)}
            className={`rounded-full transition-all duration-300 ${
              i === currentSlide
                ? 'w-5 h-1.5 bg-[#FF2300]'
                : 'w-1.5 h-1.5 bg-zinc-700 hover:bg-zinc-600'
            }`}
          />
        ))}
      </div>

      <p className="relative z-10 mt-4 text-[10px] text-zinc-700 tracking-wide">
        Swipe or use arrow keys
      </p>
    </div>
  )
}
